import { motion, useReducedMotion } from 'motion/react';
import { Download, Loader2, TriangleAlert } from 'lucide-react';
import type {
  LoadedFile,
  ScheduleState,
} from '@/components/campaign-import/import-wizard.reducer';
import { useImportErrorMessages } from '@/components/campaign-import/use-import-error-messages';
import { AnimatedNumber } from '@/components/orders-drilldown/animated-number';
import { Button } from '@/components/ui/button';
import type { CreateCampaignImportBatchResponseDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import { downloadCsvFile } from '@/lib/campaign-import/download-csv-file';
import { buildErrorsCsv } from '@/lib/campaign-import/errors-csv';

export interface ResultViewProps {
  result: CreateCampaignImportBatchResponseDto;
  file: LoadedFile | null;
  storeWebsiteDomain: string;
  schedule: ScheduleState;
  /** Confirms the whole batch (no campaign ids). */
  onSchedule: () => void;
  onViewBatch: (batchId: string) => void;
  onImportAnother: () => void;
}

function CompletionMark({ success }: { success: boolean }) {
  const reduceMotion = useReducedMotion();
  const draw = (delay: number) =>
    reduceMotion
      ? { initial: false as const }
      : {
          initial: { pathLength: 0 },
          animate: { pathLength: 1 },
          transition: {
            delay,
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1] as const,
          },
        };

  if (!success) {
    return (
      <span
        aria-hidden
        className="flex size-20 items-center justify-center rounded-full border border-amber-300/40 bg-amber-300/10"
      >
        <TriangleAlert className="size-9 text-amber-300" />
      </span>
    );
  }

  return (
    <div
      className="relative size-20"
      data-motion={reduceMotion ? 'static' : 'animated'}
    >
      {reduceMotion ? null : (
        <motion.span
          aria-hidden
          className="absolute inset-0 rounded-full bg-[#155DFC]/40"
          initial={{ scale: 0.8, opacity: 0.7 }}
          animate={{ scale: 1.6, opacity: 0 }}
          transition={{ delay: 0.5, duration: 1.4, ease: 'easeOut' }}
        />
      )}
      <svg viewBox="0 0 80 80" className="relative size-20" aria-hidden>
        <motion.circle
          cx="40"
          cy="40"
          r="34"
          fill="rgba(21,93,252,0.15)"
          stroke="#155DFC"
          strokeWidth="4"
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
          {...draw(0)}
        />
        <motion.path
          d="M26 41 L36 51 L55 30"
          fill="none"
          stroke="#fff"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...draw(0.35)}
        />
      </svg>
    </div>
  );
}

export function ResultView({
  result,
  file,
  storeWebsiteDomain,
  schedule,
  onSchedule,
  onViewBatch,
  onImportAnother,
}: ResultViewProps) {
  const { LL } = useI18nContext();
  const messages = useImportErrorMessages(storeWebsiteDomain);
  const failed = result.rejected.length;
  const batch = result.batch;
  const scheduled = schedule.status === 'done' ? schedule.result : null;
  const skippedInPast = scheduled?.skippedInPast ?? [];
  // Created but not scheduled: nothing goes out until the batch is scheduled.
  const awaitingSchedule =
    batch !== null &&
    result.createdCount > 0 &&
    (schedule.status === 'idle' || schedule.status === 'failed');

  const downloadFailures = () => {
    if (!file) return;
    const cellsByRow = new Map(
      file.rows.map((row) => [row.rowNumber, row.cells])
    );
    downloadCsvFile(
      buildErrorsCsv({
        headers: file.headers,
        rows: result.rejected.map((rejected) => ({
          rowNumber: rejected.rowNumber,
          cells: cellsByRow.get(rejected.rowNumber) ?? [],
          errors: rejected.errors,
        })),
        rowNumberHeader: LL.campaignImport.review.rowNumberHeader(),
        errorsHeader: LL.campaignImport.review.errorsHeader(),
        describeError: messages.describe,
      }),
      LL.campaignImport.result.failuresCsvFileName()
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-6">
        <CompletionMark success={result.createdCount > 0} />
        <div className="grid grid-cols-2 gap-3 sm:auto-cols-fr sm:grid-flow-col">
          <div className="rounded-lg border border-white/10 bg-white/[0.04] px-5 py-3">
            <p className="text-xs text-white/60">
              {LL.campaignImport.result.createdLabel()}
            </p>
            <p
              className="text-3xl font-semibold text-emerald-300"
              data-slot="created-count"
            >
              <AnimatedNumber
                value={result.createdCount}
                format={(n) => String(Math.round(n))}
              />
            </p>
          </div>
          {scheduled ? (
            <div className="rounded-lg border border-white/10 bg-white/[0.04] px-5 py-3">
              <p className="text-xs text-white/60">
                {LL.campaignImport.result.scheduledLabel()}
              </p>
              <p
                className="text-3xl font-semibold text-[#7FA6FF]"
                data-slot="scheduled-count"
              >
                <AnimatedNumber
                  value={scheduled.confirmedCount}
                  format={(n) => String(Math.round(n))}
                />
              </p>
            </div>
          ) : null}
          <div className="rounded-lg border border-white/10 bg-white/[0.04] px-5 py-3">
            <p className="text-xs text-white/60">
              {LL.campaignImport.result.failedLabel()}
            </p>
            <p
              className="text-3xl font-semibold text-red-300"
              data-slot="failed-count"
            >
              <AnimatedNumber
                value={failed}
                format={(n) => String(Math.round(n))}
              />
            </p>
          </div>
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {LL.campaignImport.result.summaryAnnouncement({
          created: result.createdCount,
          failed,
        })}
      </p>

      {scheduled ? (
        <p className="sr-only" role="status" aria-live="polite">
          {LL.campaignImport.result.scheduledAnnouncement({
            count: scheduled.confirmedCount,
          })}
        </p>
      ) : null}

      {schedule.status === 'sending' ? (
        <p
          role="status"
          aria-live="polite"
          data-slot="scheduling"
          className="flex items-center gap-2 text-sm text-white/80"
        >
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {LL.campaignImport.result.scheduling()}
        </p>
      ) : null}

      {scheduled && scheduled.confirmedCount > 0 ? (
        <p className="max-w-prose text-sm text-white/75">
          {LL.campaignImport.result.scheduledNote()}
        </p>
      ) : null}

      {awaitingSchedule ? (
        <section
          role="alert"
          aria-labelledby="import-draft-title"
          data-slot="draft-callout"
          className="flex flex-wrap items-center gap-4 rounded-xl border border-amber-300/60 bg-amber-300/15 p-5"
        >
          <TriangleAlert
            className="size-6 shrink-0 text-amber-300"
            aria-hidden
          />
          <div className="min-w-0 flex-1 space-y-1">
            <h3
              id="import-draft-title"
              className="text-base font-semibold text-amber-100"
            >
              {LL.campaignImport.result.draftCalloutTitle()}
            </h3>
            <p className="text-sm text-amber-50/90">
              {LL.campaignImport.result.draftCalloutBody({
                count: result.createdCount,
              })}
            </p>
            {schedule.status === 'failed' ? (
              <p className="text-sm font-medium text-red-200">
                {LL.campaignImport.result.scheduleFailed()}
              </p>
            ) : null}
          </div>
          <Button
            type="button"
            onClick={onSchedule}
            className="bg-[#155DFC] text-white hover:bg-[#155DFC]/90"
          >
            {LL.campaignImport.result.scheduleBatch()}
          </Button>
        </section>
      ) : null}

      {skippedInPast.length > 0 && batch ? (
        <section
          aria-labelledby="import-skipped-title"
          data-slot="skipped-in-past"
          className="space-y-3 rounded-xl border border-amber-300/40 bg-amber-300/10 p-5"
        >
          <h3 id="import-skipped-title" className="text-sm font-semibold">
            {LL.campaignImport.result.skippedTitle({
              count: skippedInPast.length,
            })}
          </h3>
          <ul className="max-h-40 list-disc space-y-0.5 overflow-auto pl-5 text-sm">
            {skippedInPast.map((skipped) => (
              <li key={skipped.id}>{skipped.name}</li>
            ))}
          </ul>
          <p className="text-sm text-white/75">
            {LL.campaignImport.result.skippedHint()}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => onViewBatch(batch.id)}
            className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            {LL.campaignImport.result.fixInBatch()}
          </Button>
        </section>
      ) : null}

      {result.createdCount === 0 ? (
        <p className="max-w-prose text-sm text-amber-200">
          {LL.campaignImport.result.noneCreated()}
        </p>
      ) : null}

      {failed > 0 ? (
        <section aria-labelledby="import-failures-title" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 id="import-failures-title" className="text-sm font-semibold">
              {LL.campaignImport.result.failuresTitle()}
            </h3>
            <Button
              type="button"
              variant="outline"
              onClick={downloadFailures}
              className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Download aria-hidden />
              {LL.campaignImport.result.downloadFailures()}
            </Button>
          </div>
          <ul
            data-slot="failures"
            className="max-h-72 space-y-1.5 overflow-auto rounded-lg border border-white/10 bg-black/20 p-3 text-sm"
          >
            {result.rejected.map((rejected) => (
              <li key={rejected.rowNumber} data-row-number={rejected.rowNumber}>
                <span className="font-medium tabular-nums">
                  {LL.campaignImport.result.failureRow({
                    row: rejected.rowNumber,
                  })}
                </span>
                <ul className="ml-4 list-disc text-red-200">
                  {rejected.errors.map((error) => (
                    <li key={`${error.field}-${error.code}`}>
                      {messages.describe(error)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-wrap justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onImportAnother}
          className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
        >
          {LL.campaignImport.result.importAnother()}
        </Button>
        {batch ? (
          <Button
            type="button"
            onClick={() => onViewBatch(batch.id)}
            className="bg-[#155DFC] text-white hover:bg-[#155DFC]/90"
          >
            {LL.campaignImport.result.viewBatch()}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
